import { Input } from '@components'
import { AddressSuggestion, fetchAddressSuggestions } from '@query/expenseClaimTrips'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type AddressFieldProps = {
  slug: string
  name: string
  label: string
  info?: string
  required?: boolean
  value: string
  disabled: boolean
  onTextChange: (value: string) => void
  onSelect: (suggestion: AddressSuggestion) => void
}

const newSession = () => crypto.randomUUID()
const MIN_QUERY_LENGTH = 2
const SEARCH_DELAY_MS = 400

export const AddressField: React.FC<AddressFieldProps> = ({
  slug,
  name,
  label,
  info,
  required,
  value,
  disabled,
  onTextChange,
  onSelect
}) => {
  const [t] = useTranslation()
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const session = useRef(newSession())
  const picked = useRef<string | null>(null)
  const requestId = useRef(0)

  useEffect(() => {
    const id = ++requestId.current
    if (disabled || value.trim().length < MIN_QUERY_LENGTH || value === picked.current) {
      setSuggestions([])
      return
    }
    const token = session.current
    const query = value.trim()
    const handle = window.setTimeout(() => {
      if (requestId.current !== id) return
      fetchAddressSuggestions(slug, query, token)
        .then(rows => {
          if (requestId.current === id) setSuggestions(rows)
        })
        .catch(() => {
          if (requestId.current === id) setSuggestions([])
        })
    }, SEARCH_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [disabled, slug, value])

  return (
    <Wrap>
      <Input
        name={name}
        label={label}
        info={info}
        required={required}
        value={value}
        disabled={disabled}
        autoComplete="off"
        onBlur={() => {
          requestId.current += 1
          setSuggestions([])
        }}
        onChange={event => {
          const next = event.target.value as string
          if (picked.current !== null && next !== picked.current) {
            session.current = newSession()
            picked.current = null
          }
          onTextChange(next)
        }}
      />
      {suggestions.length > 0 && (
        <List role="listbox" aria-label={t('travelAndExpenses.leg.suggestions')}>
          {suggestions.map(row => (
            <Option
              key={row.place_id}
              type="button"
              role="option"
              onMouseDown={event => {
                event.preventDefault()
                picked.current = row.text
                session.current = newSession()
                setSuggestions([])
                onSelect(row)
              }}
            >
              {row.text}
            </Option>
          ))}
        </List>
      )}
    </Wrap>
  )
}

const Wrap = styled.div`
  position: relative;
`

const List = styled.div`
  position: absolute;
  z-index: 2;
  top: 100%;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  padding: ${({ theme }) => theme.spacing.xxs}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const Option = styled.button`
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`
